import { ReactNode, useState, useEffect, useRef } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useUserContext } from '../contexts/UserContext';
import { useApi } from '../utils/useApi';
import { useBranch } from '../contexts/BranchContext';
import toast from 'react-hot-toast';
import FloatingActionButtons from './FloatingActionButtons';
import HelpModal from './HelpModal';
import PwaInstallModal from './PwaInstallModal';
import ImpersonationBanner from './ImpersonationBanner';
import { HelpCircle, Smartphone, Sparkles, Dumbbell, Calendar, Wallet, BarChart3, X, LayoutDashboard, ShoppingBag, Package, Radar, Receipt, Building2, ChevronDown, Check } from 'lucide-react';

interface ComingSoonModalProps {
  item: { name: string; description: string; icon: React.ReactNode } | null;
  onClose: () => void;
}

const ComingSoonModal = ({ item, onClose }: ComingSoonModalProps) => {
  if (!item) return null;
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[2000] p-4 animate-in fade-in duration-200" {...backdropClose(onClose)}>
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in duration-300"
        onClick={e => e.stopPropagation()}
      >
        <div className="relative p-8 text-center">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="w-20 h-20 bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm border border-gray-100">
            <div className="scale-125">
              {item.icon}
            </div>
          </div>

          <h3 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">{item.name}</h3>

          <div className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-600 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border border-amber-200 mb-6 shadow-sm">
            <Sparkles className="w-3 h-3" />
            Coming Soon
          </div>

          <p className="text-gray-600 leading-relaxed mb-8 font-medium">
            {item.description}
          </p>

          <button
            onClick={onClose}
            className="w-full py-4 bg-zinc-950 text-[#AAD816] rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-[#AAD816] hover:text-zinc-950 transition-all shadow-xl shadow-zinc-200 active:scale-[0.98] transform transition-transform"
          >
            Got it, thanks!
          </button>
        </div>

        <div className="bg-zinc-50 py-3 border-t border-gray-100 italic">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Gympe Innovation Lab</p>
        </div>
      </div>
    </div>
  );
};

// Gym-themed avatars (same as UserProfilePage)
const GYM_AVATARS: Record<string, { emoji: string; color: string }> = {
  'dumbbell-1': { emoji: '🏋️', color: 'from-blue-500 to-blue-600' },
  'dumbbell-2': { emoji: '💪', color: 'from-purple-500 to-purple-600' },
  'barbell-1': { emoji: '🏋️‍♂️', color: 'from-red-500 to-red-600' },
  'barbell-2': { emoji: '🏋️‍♀️', color: 'from-green-500 to-green-600' },
  'trophy-1': { emoji: '🏆', color: 'from-yellow-500 to-yellow-600' },
  'trophy-2': { emoji: '🥇', color: 'from-orange-500 to-orange-600' },
  'fitness-1': { emoji: '💪', color: 'from-indigo-500 to-indigo-600' },
  'fitness-2': { emoji: '🔥', color: 'from-pink-500 to-pink-600' },
  'gym-1': { emoji: '🏋️', color: 'from-teal-500 to-teal-600' },
  'gym-2': { emoji: '⚡', color: 'from-cyan-500 to-cyan-600' },
  'strength-1': { emoji: '💪', color: 'from-amber-500 to-amber-600' },
  'strength-2': { emoji: '🎯', color: 'from-violet-500 to-violet-600' },
};

// Features that are enabled by default if the key is missing from the plan
const CORE_FEATURES = [
  'dashboard',
  'attendance',
  'notifications',
  'plans',
  'purchases',
  'branches',
  'members',
  'team',
  'settings'
];

function UserAvatar({ user }: { user: any }) {
  const { userContext } = useUserContext();
  const avatarId = userContext?.user?.avatar || null;
  const avatar = avatarId ? GYM_AVATARS[avatarId] : null;
  const initials = (user?.user_metadata?.name || user?.email?.split('@')[0] || 'U').charAt(0).toUpperCase();

  if (avatar) {
    return (
      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatar.color} flex items-center justify-center text-lg shadow-sm`}>
        {avatar.emoji}
      </div>
    );
  }

  return (
    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm">
      {initials}
    </div>
  );
}

function UserDisplayName({ user }: { user: any }) {
  const { userContext } = useUserContext();

  // Priority: userContext displayName > userContext name > metadata name > email
  const displayName = userContext?.user?.displayName ||
    userContext?.user?.name ||
    user?.user_metadata?.name ||
    user?.email?.split('@')[0] ||
    'User';

  return <>{displayName}</>;
}

function UserRoleDisplay() {
  const { userContext } = useUserContext();
  const { user } = useAuth();

  // Get roles from userContext
  const roles = userContext?.roles || [];

  // Role priority: owner > admin > trainer > staff > member
  const rolePriority: Record<string, number> = {
    'owner': 1,
    'admin': 2,
    'trainer': 3,
    'staff': 4,
    'member': 5
  };

  // Get the highest priority role
  let primaryRole = 'Member';
  if (roles.length > 0) {
    const sortedRoles = roles
      .map(r => ({ role: r.role.toLowerCase(), priority: rolePriority[r.role.toLowerCase()] || 99 }))
      .sort((a, b) => a.priority - b.priority);

    primaryRole = sortedRoles[0]?.role || 'Member';
  } else {
    // Fallback to metadata role if no roles in context
    primaryRole = user?.user_metadata?.role || 'Member';
  }

  // Capitalize first letter
  return <>{primaryRole.charAt(0).toUpperCase() + primaryRole.slice(1)}</>;
}

interface LayoutProps {
  children: ReactNode;
  pageTitle?: string;
  pageSubtitle?: string;
  hideTopBar?: boolean;
}

export default function Layout({ children, pageTitle: _pageTitle, pageSubtitle: _pageSubtitle, hideTopBar: _hideTopBar }: LayoutProps) {
  const { user, signOut } = useAuth();
  const { userContext, loading, hasResourcePermission, isOwner } = useUserContext();
  const { fetchWithAuth } = useApi();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
  const [branchMenuOpen, setBranchMenuOpen] = useState(false);
  const branchMenuRef = useRef<HTMLDivElement>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [comingSoonItem, setComingSoonItem] = useState<{ name: string; description: string; icon: React.ReactNode } | null>(null);

  useEffect(() => {
    const checkPwaStatus = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
      setShowInstallButton(!isStandalone && isMobile);
    };

    checkPwaStatus();
    window.addEventListener('resize', checkPwaStatus);
    return () => window.removeEventListener('resize', checkPwaStatus);
  }, []);
  // Default to minimized if screen is lg but not xl (iPad landscape/small laptop)
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024 && window.innerWidth < 1280;
    }
    return false;
  });

  // Determine active gym plan
  const activeGymRole = userContext?.roles.find(r => r.plan);
  const activeGymPlan = activeGymRole?.plan;

  const gymId = userContext?.accessibleGyms?.[0];

  // Fetch gym details for display name
  const { data: gymData } = useQuery({
    queryKey: ['gym-settings', gymId],
    queryFn: async () => {
      const response = await fetchWithAuth(`/api/gyms/${gymId}`);
      return response.data;
    },
    enabled: !!gymId,
  });

  // Get gym display name with better fallbacks to prevent "GYMPE" flickering
  // Priority: Gym Settings Display Name > Gym Settings Name > Role Gym Name > Fallback
  const gymDisplayName = gymData?.displayName ||
    gymData?.name ||
    userContext?.roles.find(r => r.gymId === gymId)?.gymName ||
    'GYMPE';

  const messageCredits = gymData?.subscription?.messageCredits != null
    ? Number(gymData.subscription.messageCredits)
    : null;

  // Branch scope selector shown as a sub-line under the gym name (replaces the
  // old top-left switcher). Shares the branch query cache so it adds no request.
  const { selectedBranchId, setSelectedBranchId } = useBranch();
  const { data: allTopBarBranches = [] } = useQuery<Array<{ id: string; name: string; displayName: string | null; status?: string }>>({
    queryKey: ['branch-switcher-branches'],
    queryFn: async () => {
      const res = await fetchWithAuth('/api/branches');
      return (res.data || []).filter((b: any) => (b.status ?? 'active') !== 'inactive');
    },
    enabled: !!gymId,
  });
  // Restrict to the user's accessible branches (owners see all).
  const accessibleBranchIds = userContext?.accessibleBranches || [];
  const topBarBranches = isOwner
    ? allTopBarBranches
    : allTopBarBranches.filter(b => accessibleBranchIds.includes(b.id));
  const currentBranch = selectedBranchId ? topBarBranches.find(b => b.id === selectedBranchId) : null;
  const branchLabel = (b: { name: string; displayName: string | null }) => b.displayName || b.name;
  const branchSubtext = currentBranch ? branchLabel(currentBranch) : 'All Branches';

  // Drop a stale selection (access revoked) back to "All Branches".
  useEffect(() => {
    if (selectedBranchId && topBarBranches.length > 0 && !topBarBranches.some(b => b.id === selectedBranchId)) {
      setSelectedBranchId(null);
    }
  }, [topBarBranches, selectedBranchId, setSelectedBranchId]);

  // Close the branch menu on outside click.
  useEffect(() => {
    if (!branchMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (branchMenuRef.current && !branchMenuRef.current.contains(e.target as Node)) setBranchMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [branchMenuOpen]);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      // Force redirect to login page
      window.location.href = '/login';
    } catch (error: any) {
      toast.error('Failed to sign out');
      console.error('Sign out error:', error);
      // Even if sign out fails, redirect to login
      window.location.href = '/login';
    }
  };

  // Modern SVG Icons
  const icons = {
    DashboardIcon: (
      <Sparkles className="w-5 h-5 text-indigo-500" />
    ),
    AttendanceIcon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    NotificationsIcon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    PlansIcon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    PurchasesIcon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
    ),
    BranchesIcon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    MembersIcon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    TeamIcon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    SettingsIcon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    ReportsIcon: (
      <BarChart3 className="w-5 h-5 text-emerald-500" />
    ),
    TrainingIcon: (
      <Dumbbell className="w-5 h-5 text-purple-500" />
    ),
    ClassIcon: (
      <Calendar className="w-5 h-5 text-orange-500" />
    ),
    ExpenseIcon: (
      <Wallet className="w-5 h-5 text-red-500" />
    ),
    GstIcon: (
      <Receipt className="w-5 h-5 text-teal-500" />
    ),
    LayoutDashboard: (
      <LayoutDashboard className="w-5 h-5 text-blue-500" />
    ),
    ServicesIcon: (
      <ShoppingBag className="w-5 h-5 text-violet-500" />
    ),
    ProductsIcon: (
      <Package className="w-5 h-5 text-amber-500" />
    ),
    ReachIcon: (
      <Radar className="w-5 h-5 text-fuchsia-500" />
    ),
  };

  const navigation = [
    // First section - Core daily operations
    { name: 'AI', href: '/ai', icon: 'DashboardIcon', section: 'core', featureKey: 'dashboard' },
    { name: 'Reach', href: '/reach', icon: 'ReachIcon', section: 'core', featureKey: 'dashboard', badge: 'AI' },
    { name: 'Dashboard', href: '/dashboard', icon: 'LayoutDashboard', section: 'core', featureKey: 'dashboard' },
    { name: 'Attendance', href: '/attendance', icon: 'AttendanceIcon', section: 'core', featureKey: 'attendance' },
    { name: 'Notifications', href: '/notifications', icon: 'NotificationsIcon', section: 'core', featureKey: 'notifications' },

    // Second section - Business operations
    { name: 'Plans', href: '/plans', icon: 'PlansIcon', section: 'business', featureKey: 'plans' },
    { name: 'Services', href: '/services', icon: 'ServicesIcon', section: 'business', featureKey: 'purchases' },
    { name: 'Products', href: '/products', icon: 'ProductsIcon', section: 'business', featureKey: 'purchases' },
    { name: 'Purchases', href: '/purchases', icon: 'PurchasesIcon', section: 'business', featureKey: 'purchases' },
    { name: 'Bookings', href: '/schedule', icon: 'ClassIcon', section: 'business', featureKey: 'class_scheduling' },

    // Third section - Finance (owner-only: the API gates these behind manage_billing)
    ...(userContext?.roles.some(r => r.role === 'owner')
      ? [
          { name: 'Expenses', href: '/expenses', icon: 'ExpenseIcon', section: 'finance', featureKey: 'expense_tracking' },
          { name: 'GST Report', href: '/gst-report', icon: 'GstIcon', section: 'finance', featureKey: 'gst_reports' },
        ]
      : []),

    // Fourth section - Management
    { name: 'Branches', href: '/branches', icon: 'BranchesIcon', section: 'management', featureKey: 'branches' },
    { name: 'Members', href: '/members', icon: 'MembersIcon', section: 'management', featureKey: 'members' },
    { name: 'Reports', href: '/reports', icon: 'ReportsIcon', section: 'management', featureKey: 'reports' },
    { name: 'Team', href: '/team', icon: 'TeamIcon', section: 'management', featureKey: 'team' },
    // HRMS: owners always see it; admins only if they have at least one HRMS tab permission
    ...(userContext?.roles.some(r => r.role === 'owner')
      ? [{ name: 'HRMS', href: '/hrms', icon: 'TeamIcon', section: 'management', featureKey: 'hrms' }]
      : userContext?.roles.some(r => r.role === 'admin' && (r.hrmsPermissions?.length ?? 0) > 0)
        ? [{ name: 'HRMS', href: '/hrms', icon: 'TeamIcon', section: 'management', featureKey: 'hrms' }]
        : []),
    // Only show Settings for Owners, hide for Admins
    ...(userContext?.roles.some(r => r.role === 'owner')
      ? [{ name: 'Settings', href: '/settings/gym', icon: 'SettingsIcon', section: 'management', featureKey: 'settings' }]
      : [])
  ];

  // Hide nav items the team member has no resource permission for. Owners always
  // pass (hasResourcePermission short-circuits). The backend enforces the same.
  const RESOURCE_BY_HREF: Record<string, 'reports' | 'expenses' | 'purchases' | 'ai' | 'dashboard' | 'notifications' | 'attendance'> = {
    '/ai': 'ai',
    '/reach': 'ai',
    '/dashboard': 'dashboard',
    '/attendance': 'attendance',
    '/notifications': 'notifications',
    '/purchases': 'purchases',
    '/reports': 'reports',
    '/expenses': 'expenses',
  };
  const visibleNavigation = navigation.filter(item => {
    const rk = RESOURCE_BY_HREF[(item as any).href];
    return !rk || hasResourcePermission(rk);
  });

  const comingSoonNav = [
    {
      name: 'Training Plans',
      icon: 'TrainingIcon',
      description: 'Create and assign professional workout programs, track member progress, and build custom exercise libraries.'
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="h-[100dvh] bg-gray-50 flex overflow-hidden relative">
      <ImpersonationBanner />
      {/* Sidebar Overlay (Mobile) */}
      <div
        className={`fixed inset-0 bg-black z-[90] lg:hidden transition-opacity duration-300 ${isSidebarOpen ? 'opacity-50 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-[100] bg-white shadow-xl border-r border-gray-200 flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out transform
        lg:relative lg:translate-x-0 lg:shadow-none
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${isSidebarMinimized ? 'lg:w-20' : 'lg:w-64'}
        w-64
      `}>
        {/* Toggle Button (Desktop Only) */}
        <button
          onClick={() => setIsSidebarMinimized(!isSidebarMinimized)}
          className="hidden lg:flex absolute -right-3 top-20 bg-white border border-gray-200 rounded-full p-1 shadow-md hover:bg-gray-50 text-gray-500 z-50 items-center justify-center w-6 h-6"
          title={isSidebarMinimized ? "Expand Sidebar" : "Minimize Sidebar"}
        >
          <svg className={`w-3 h-3 transition-transform duration-300 ${isSidebarMinimized ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        {/* Logo */}
        <div className={`
          border-b border-gray-200 flex flex-col transition-all duration-300
          ${isSidebarMinimized ? 'items-center px-2 py-4' : 'p-6 space-y-4'}
        `}>
          <div className={`flex items-center ${isSidebarMinimized ? 'justify-center' : 'justify-between'}`}>
            <Link to="/dashboard" className={`flex flex-col ${isSidebarMinimized ? 'items-center' : 'items-start'} group transition-all duration-300`}>
              <div className={`
                flex items-center transition-all duration-300
                ${isSidebarMinimized ? 'justify-center w-12 h-12 bg-white rounded-xl shadow-sm border border-gray-100 p-1.5' : 'justify-start w-full h-8'}
              `}>
                <img
                  src="/gympe.svg"
                  alt="Gympe"
                  className={`transition-all duration-300 ${isSidebarMinimized ? 'w-full h-full object-contain' : 'h-7 w-auto'}`}
                />
              </div>

              <div className={`
                mt-1 transition-all duration-300 overflow-hidden
                ${isSidebarMinimized ? 'h-0 opacity-0' : 'h-auto opacity-100'}
              `}>
                {activeGymPlan && (
                  <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider ${activeGymPlan.name === 'Pro' ? 'bg-zinc-950 text-[#bef264] border border-zinc-800' :
                    activeGymPlan.name === 'Standard' ? 'bg-zinc-900 text-zinc-100' :
                      'bg-zinc-100 text-zinc-600 border border-zinc-200'
                    }`}>
                    {activeGymPlan.name}
                  </span>
                )}
              </div>
            </Link>

            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden p-2 rounded-full bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors border border-gray-100 ml-2"
              aria-label="Close sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 overflow-y-auto custom-scrollbar overflow-x-hidden">
          {/* Note: Map sections to reduce duplication and handle minimized state */}
          {[
            { title: 'Core Operations', items: visibleNavigation.filter(item => item.section === 'core') },
            { title: 'Business', items: visibleNavigation.filter(item => item.section === 'business') },
            { title: 'Finance', items: visibleNavigation.filter(item => item.section === 'finance') },
            { title: 'Management', items: visibleNavigation.filter(item => item.section === 'management') },
            { title: 'Coming Soon', items: comingSoonNav, isComingSoon: true }
          ].filter(section => section.items.length > 0).map((section, idx) => (
            <div key={section.title} className={idx > 0 ? "mt-6" : ""}>
              {!isSidebarMinimized && (
                <h3 className="px-6 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 transition-opacity duration-200 whitespace-nowrap overflow-hidden flex items-center gap-2">
                  {section.title}
                  {(section as any).isComingSoon && <Sparkles className="w-3 h-3 text-amber-400" />}
                </h3>
              )}
              <div className="space-y-1 px-3">
                {section.items.map((item) => {
                  if ((section as any).isComingSoon) {
                    return (
                      <button
                        key={item.name}
                        onClick={() => setComingSoonItem({ ...(item as any), icon: icons[item.icon as keyof typeof icons] })}
                        title={isSidebarMinimized ? item.name : undefined}
                        className={`
                          w-full flex items-center ${isSidebarMinimized ? 'justify-center px-2' : 'justify-between px-4'} py-3 
                          rounded-xl text-sm font-medium transition-all duration-200 group relative
                          text-gray-400 hover:text-gray-600 hover:bg-gray-50/80
                        `}
                      >
                        <div className={`flex items-center ${isSidebarMinimized ? 'justify-center w-full' : 'space-x-3'}`}>
                          <span className="text-gray-300 group-hover:text-gray-400 transition-colors duration-200 flex-shrink-0">
                            {icons[item.icon as keyof typeof icons]}
                          </span>

                          <span className={`
                            transition-all duration-300 overflow-hidden whitespace-nowrap text-left
                            ${isSidebarMinimized ? 'w-0 opacity-0 ml-0' : 'w-auto opacity-100'}
                          `}>
                            {item.name}
                          </span>
                        </div>

                        {!isSidebarMinimized && (
                          <span className="text-[10px] font-black bg-amber-50 text-amber-500 px-2 py-0.5 rounded tracking-tighter uppercase italic border border-amber-100 shadow-sm ml-2">
                            Soon
                          </span>
                        )}
                      </button>
                    );
                  }

                  const featureValue = activeGymPlan?.features?.[(item as any).featureKey];
                  const isFeatureEnabled = featureValue !== undefined ? featureValue : CORE_FEATURES.includes((item as any).featureKey);
                  const isEnabled = !loading && (!activeGymPlan || isFeatureEnabled);

                  return (
                    <Link
                      key={item.name}
                      to={isEnabled ? (item as any).href : '#'}
                      onClick={(e) => {
                        if (!isEnabled) {
                          e.preventDefault();
                          toast.error(`${item.name} is a Premium feature. Upgrade to unlock!`);
                        } else {
                          setIsSidebarOpen(false);
                        }
                      }}
                      title={isSidebarMinimized ? item.name : undefined}
                      className={`
                        flex items-center ${isSidebarMinimized ? 'justify-center px-2' : 'justify-between px-4'} py-3 
                        rounded-xl text-sm font-medium transition-all duration-200 group relative
                        ${isActive((item as any).href) && isEnabled
                          ? 'bg-blue-50 text-blue-700 shadow-sm' + (!isSidebarMinimized ? ' border-r-2 border-blue-600' : '')
                          : isEnabled
                            ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            : 'text-gray-300 cursor-not-allowed grayscale'
                        }
                      `}
                    >
                      <div className={`flex items-center ${isSidebarMinimized ? 'justify-center w-full' : 'space-x-3'}`}>
                        <span className={`
                          ${isActive((item as any).href) && isEnabled ? 'text-blue-600' : isEnabled ? 'text-gray-400 group-hover:text-gray-600' : 'text-gray-200'}
                          transition-colors duration-200 flex-shrink-0
                        `}>
                          {icons[item.icon as keyof typeof icons]}
                        </span>

                        <span className={`
                          transition-all duration-300 overflow-hidden whitespace-nowrap
                          ${isSidebarMinimized ? 'w-0 opacity-0 ml-0' : 'w-auto opacity-100'}
                        `}>
                          {item.name}
                        </span>
                      </div>

                      {!isSidebarMinimized && !isEnabled && !loading && (
                        <span className="text-[10px] font-black bg-indigo-50 text-indigo-500 px-2 py-0.5 rounded tracking-tighter uppercase italic border border-indigo-100 shadow-sm ml-2">
                          Pro
                        </span>
                      )}

                      {!isSidebarMinimized && isEnabled && (item as any).badge && (
                        <span className="text-[10px] font-black bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white px-2 py-0.5 rounded tracking-tighter uppercase shadow-sm ml-2">
                          {(item as any).badge}
                        </span>
                      )}

                      {!isSidebarMinimized && (item as any).status === 'coming-soon' && (
                        <span className="text-[10px] font-black bg-amber-50 text-amber-500 px-2 py-0.5 rounded tracking-tighter uppercase italic border border-amber-100 shadow-sm ml-2">
                          Soon
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-gray-200">
          <Link
            to="/profile"
            onClick={() => setIsSidebarOpen(false)}
            className={`
              flex items-center rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer
              ${isSidebarMinimized ? 'justify-center p-2' : 'p-3 space-x-3'}
            `}
            title={isSidebarMinimized ? "Profile" : undefined}
          >
            <UserAvatar user={user} />

            <div className={`
              flex-1 min-w-0 transition-all duration-300 overflow-hidden
              ${isSidebarMinimized ? 'w-0 opacity-0 hidden' : 'w-auto opacity-100 block'}
            `}>
              <p className="text-sm font-medium text-gray-900 break-words line-clamp-2 leading-tight">
                <UserDisplayName user={user} />
              </p>
              <p className="text-xs text-gray-500 truncate mt-0.5">
                <UserRoleDisplay />
              </p>
            </div>

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSignOut();
              }}
              className={`
                text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-200
                ${isSidebarMinimized ? 'hidden' : 'p-1'}
              `}
              title="Sign Out"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Top Bar */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 transition-all">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden mr-3 p-2 rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
            <div className="flex items-center ml-2 flex-shrink-0 gap-3 sm:gap-6">
              {showInstallButton && (
                <button
                  onClick={() => setIsInstallModalOpen(true)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition-all group"
                  title="Install App"
                >
                  <Smartphone className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  <span className="hidden sm:inline text-xs font-bold">Install App</span>
                </button>
              )}
              <button
                onClick={() => setIsHelpOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-all group"
                title="Help Guide"
              >
                <HelpCircle className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline text-xs font-bold">Help Guide</span>
              </button>
              {messageCredits !== null && (
                <div
                  title={`Message credits left: ₹${messageCredits.toFixed(2)}`}
                  className={`flex flex-col items-center px-2 py-1 rounded-lg border ${messageCredits > 50
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : messageCredits > 25
                        ? 'bg-orange-50 border-orange-200 text-orange-600'
                        : 'bg-red-50 border-red-200 text-red-600'
                    }`}
                >
                  <span className="text-xs font-black leading-none">₹{messageCredits.toFixed(2)}</span>
                  <span className="text-[8px] font-medium opacity-70 leading-none mt-0.5">credits</span>
                </div>
              )}
              <div className="text-right">
                <div className="text-sm sm:text-lg font-bold text-gray-900 truncate max-w-[120px] sm:max-w-none">{gymDisplayName}</div>
                {/* Branch scope: a dropdown when the user can access >1 branch,
                    otherwise a static label (or nothing when there are none). */}
                {topBarBranches.length > 1 ? (
                  <div ref={branchMenuRef} className="relative flex justify-end">
                    <button
                      onClick={() => setBranchMenuOpen(o => !o)}
                      className="flex items-center gap-1 text-[10px] sm:text-xs text-blue-600 font-semibold hover:text-blue-700 transition-colors max-w-[120px] sm:max-w-[180px]"
                      title="Switch branch scope"
                    >
                      <Building2 className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{branchSubtext}</span>
                      <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${branchMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {branchMenuOpen && (
                      <div className="absolute right-0 top-full mt-1.5 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 z-[120] max-h-[70vh] overflow-y-auto custom-scrollbar text-left">
                        <button
                          onClick={() => { setSelectedBranchId(null); setBranchMenuOpen(false); }}
                          className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                        >
                          <span className={`font-semibold ${!selectedBranchId ? 'text-blue-700' : 'text-gray-700'}`}>All Branches</span>
                          {!selectedBranchId && <Check className="w-4 h-4 text-blue-600" />}
                        </button>
                        <div className="my-1 border-t border-gray-100" />
                        {topBarBranches.map(b => {
                          const active = b.id === selectedBranchId;
                          return (
                            <button
                              key={b.id}
                              onClick={() => { setSelectedBranchId(b.id); setBranchMenuOpen(false); }}
                              className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                            >
                              <span className={`truncate ${active ? 'font-semibold text-blue-700' : 'text-gray-700'}`}>{branchLabel(b)}</span>
                              {active && <Check className="w-4 h-4 flex-shrink-0 text-blue-600" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : topBarBranches.length === 1 ? (
                  <div className="flex items-center justify-end gap-1 text-[10px] sm:text-xs text-blue-600 font-semibold truncate max-w-[120px] sm:max-w-none">
                    <Building2 className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{branchLabel(topBarBranches[0])}</span>
                  </div>
                ) : null}
                <div className="hidden xs:block text-[8px] sm:text-xs text-gray-400 font-medium italic">powered by GYMPE</div>
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto bg-gray-50">
          <div className="max-w-7xl mx-auto pb-24 md:pb-0">
            {children}
          </div>
        </main>

        {/* Floating Action Buttons */}
        <FloatingActionButtons />
      </div>

      <HelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
      <PwaInstallModal isOpen={isInstallModalOpen} onClose={() => setIsInstallModalOpen(false)} />
      <ComingSoonModal item={comingSoonItem} onClose={() => setComingSoonItem(null)} />
    </div >
  );
}